import { createContext, ReactNode, useCallback } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';

// project import
import config from '../config';

// types
import {
  CompanySelection,
  CustomizationProps,
  FontFamily,
  I18n,
  PresetColor,
  ThemeDirection,
  ThemeMode,
} from '../config';

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeContainer: () => {},
  onChangeLocalization: (lang: I18n) => {},
  onChangeMode: (mode: ThemeMode) => {},
  onChangePresetColor: (theme: PresetColor) => {},
  onChangeDirection: (direction: ThemeDirection) => {},
  onChangeMiniDrawer: (miniDrawer: boolean) => {},
  onChangeFontFamily: (fontFamily: FontFamily) => {},
  onChangeCompany: (companyDirection: CompanySelection, presetColor) => {},
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useLocalStorage('becks-admin-config', initialState);

  const onChangeContainer = useCallback(() => {
    setConfig((prevConfig: CustomizationProps) => ({
      ...prevConfig,
      container: !prevConfig.container,
    }));
  }, [setConfig]);

  const onChangeLocalization = useCallback(
    (lang: I18n) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        i18n: lang,
      }));
    },
    [setConfig],
  );

  const onChangeMode = useCallback(
    (mode: ThemeMode) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        mode,
      }));
    },
    [setConfig],
  );

  const onChangePresetColor = useCallback(
    (theme: PresetColor) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        presetColor: theme,
      }));
    },
    [setConfig],
  );

  const onChangeDirection = useCallback(
    (direction: ThemeDirection) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        themeDirection: direction,
      }));
    },
    [setConfig],
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer: boolean) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        miniDrawer,
      }));
    },
    [setConfig],
  );

  const onChangeFontFamily = useCallback(
    (fontFamily: FontFamily) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        fontFamily,
      }));
    },
    [setConfig],
  );

  const onChangeCompany = useCallback(
    (companyDirection: CompanySelection, presetColor: PresetColor) => {
      setConfig((prevConfig: CustomizationProps) => ({
        ...prevConfig,
        presetColor,
        companyDirection,
      }));
    },
    [setConfig],
  );

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeFontFamily,
        onChangeCompany,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
